import { Module } from 'vuex'

import { DashboardUseCase } from '@/application/dashboard/dashboardUseCase'
import { CategoryRating } from '@/domain/entities/dashboard/categoryRating'
import { DashboardRepository } from '@/domain/repositories/dashboard/DashboardRepository'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'

export type CategoryRatingState = {
  userWhatCategoryRatingList: CategoryRating[][]
  userHowCategoryRatingList: CategoryRating[][]
  userProgrammingCategoryRatingList: CategoryRating[][]
  isLoading: boolean
  error: Error | null
}

export const categoryRatingModule = (repository: DashboardRepository): Module<CategoryRatingState, any> => {
  const usecase = new DashboardUseCase(repository)

  return {
    namespaced: true,
    state: {
      userWhatCategoryRatingList: [],
      userHowCategoryRatingList: [],
      userProgrammingCategoryRatingList: [],
      isLoading: false,
      error: null
    },
    mutations: {
      addUserWhatCategoryRating(state, { category }: { category: CategoryRating[] }) {
        state.userWhatCategoryRatingList.push(category)
      },
      addUserHowCategoryRating(state, { category }: { category: CategoryRating[] }) {
        state.userHowCategoryRatingList.push(category)
      },
      addUserProgrammingCategoryRating(state, { category }: { category: CategoryRating[] }) {
        state.userProgrammingCategoryRatingList.push(category)
      },
      removeUserCategories(state) {
        state.userWhatCategoryRatingList.splice(0)
        state.userHowCategoryRatingList.splice(0)
        state.userProgrammingCategoryRatingList.splice(0)
      },
      removeTargetUsers(state) {
        state.userWhatCategoryRatingList.splice(1)
        state.userHowCategoryRatingList.splice(1)
        state.userProgrammingCategoryRatingList.splice(1)
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: Error | null) {
        state.error = error
      }
    },
    actions: {
      async addUserCategoryRating({ state, commit }, { userId }: { userId: string }) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          const res = await usecase.getUserCategoryRatingList(userId)
          commit('addUserWhatCategoryRating', {
            category: res.whatCategoryRatingList
          })
          commit('addUserHowCategoryRating', {
            category: res.howCategoryRatingList
          })
          commit('addUserProgrammingCategoryRating', {
            category: res.programmingCategoryRatingList
          })
        } catch (e) {
          commit('addUserWhatCategoryRating', {
            category: []
          })
          commit('addUserHowCategoryRating', {
            category: []
          })
          commit('addUserProgrammingCategoryRating', {
            category: []
          })
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async addUserCategoryRatingByLanguage(
        { state, commit },
        { userId, programLanguage }: { userId: string; programLanguage: ProgramLanguageType | 'All' }
      ) {
        commit('setIsLoading', true)
        commit('setError', null)

        try {
          const res = await usecase.getUserCategoryRatingListByLanguage(userId, programLanguage)

          commit('addUserWhatCategoryRating', {
            category: res.whatCategoryRatingList
          })
          commit('addUserHowCategoryRating', {
            category: res.howCategoryRatingList
          })
          commit('addUserProgrammingCategoryRating', {
            category: res.programmingCategoryRatingList
          })
        } catch (e) {
          commit('addUserWhatCategoryRating', {
            category: []
          })
          commit('addUserHowCategoryRating', {
            category: []
          })
          commit('addUserProgrammingCategoryRating', {
            category: []
          })
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      userWhatCategoryRatingList: state => state.userWhatCategoryRatingList,
      userHowCategoryRatingList: state => state.userHowCategoryRatingList,
      userProgrammingCategoryRatingList: state => state.userProgrammingCategoryRatingList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
